import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { Menu as MenuIcon, Fullscreen, FullscreenExit, Person, Key, Logout } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../contexts/PermissionContext';
import Navigation from './Navigation';

interface LayoutProps {
    layout: 'empty' | 'classic' | 'centered' | 'enterprise' | 'material' | 'modern' | 'classy' | 'compact' | 'dense' | 'futuristic' | 'thin';
}

const Layout: React.FC<LayoutProps> = ({ layout }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { signOut } = useAuth();
    const { currentAdmin } = usePermission();
    const [navOpen, setNavOpen] = useState(true);
    const [isScreenSmall, setIsScreenSmall] = useState(window.innerWidth < 1280);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsScreenSmall(window.innerWidth < 1280);
            if (window.innerWidth >= 1280) {
                setNavOpen(true);
            } else {
                setNavOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleSignOut = async () => {
        handleMenuClose();
        await signOut();
        navigate('/sign-in');
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    if (layout === 'empty') {
        return (
            <div className="w-full min-h-screen">
                <Outlet />
            </div>
        );
    }

    if (layout === 'classic') {
        return (
            <div className="w-full min-h-screen bg-[linear-gradient(180deg,rgba(14,165,233,0.12)_0%,rgba(99,102,241,0.00)_68.46%)]">
                {/* Navigation Sidebar - Fixed on scroll (desktop) */}
                {!isScreenSmall && (
                    <div 
                        className={`fixed top-0 left-0 z-40 min-h-screen h-screen transition-all duration-300 bg-gray-800 ${navOpen ? 'w-[280px]' : 'w-0'}`}
                        style={{ overflow: 'hidden' }}
                    >
                        <Navigation 
                            open={navOpen} 
                            onClose={() => setNavOpen(false)}
                            mode="side"
                        />
                    </div>
                )}

                {/* Mobile Navigation Overlay */}
                {isScreenSmall && (
                    <Navigation 
                        open={navOpen} 
                        onClose={() => setNavOpen(false)}
                        mode="over"
                    />
                )}

                {/* Main Content Area - margin when sidebar open */}
                <div 
                    className="flex flex-col flex-1 min-w-0 min-h-screen transition-all duration-300"
                    style={{ marginLeft: !isScreenSmall && navOpen ? 280 : 0 }}
                >
                    {/* Header - Fixed at top */}
                    <div className="relative flex flex-0 items-center w-full h-16 px-4 md:px-6 z-50 shadow-sm border-b bg-white print:hidden">
                        {/* Navigation toggle button */}
                        <IconButton
                            onClick={() => setNavOpen(!navOpen)}
                            className="mat-icon-button"
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Components */}
                        <div className="flex items-center pl-2 ml-auto space-x-0.5 sm:space-x-2">
                            {/* Fullscreen */}
                            <IconButton onClick={toggleFullscreen} className="md:block">
                                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                            </IconButton>

                            {/* User Menu */}
                            <div>
                                <button
                                    onClick={handleMenuOpen}
                                    className="flex items-center space-x-2 px-2 py-1 bg-[#0EA5E9] hover:bg-[#0284C7] transition rounded"
                                >
                                    <img 
                                        src="/assets/images/user.png" 
                                        alt="User Avatar" 
                                        className="w-8 h-8 mr-2 rounded-full object-cover" 
                                    />
                                    <span className="text-sm font-bold text-white">
                                        {currentAdmin?.name || 'User'}
                                    </span>
                                    <span className="material-icons text-white text-xl">arrow_drop_down</span>
                                </button>

                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                >
                                    <MenuItem disabled>
                                        <div className="flex flex-col text-left leading-tight">
                                            <span className="text-xs text-gray-400">Signed in as</span>
                                            <strong className="text-sm text-black">
                                                {currentAdmin?.email || 'user@example.com'}
                                            </strong>
                                        </div>
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem component={Link} to="/profile" onClick={handleMenuClose}>
                                        <ListItemIcon>
                                            <Person fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Profile</ListItemText>
                                    </MenuItem>
                                    <MenuItem component={Link} to="/change-password" onClick={handleMenuClose}>
                                        <ListItemIcon>
                                            <Key fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Change Password</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={handleSignOut}>
                                        <ListItemIcon>
                                            <Logout fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>Sign out</ListItemText>
                                    </MenuItem>
                                </Menu>
                            </div>
                        </div>
                    </div>

                    {/* Content - Scrollable */}
                    <div className="flex flex-col flex-1 overflow-y-auto bg-white">
                        <Outlet />
                    </div>
                </div>
            </div>
        );
    }

            // Default layout
            return (
                <div className="w-full min-h-screen">
                    <Outlet />
                </div>
            );
        };

export default Layout;
